/**
 * Trimmer Section with GSAP ScrollTrigger
 * Handles 4-state transitions on desktop with pinned scrolling
 * Mobile: Shows all states in column layout
 */

"use strict";

function initTrimmerMobileSection() {
  // Mobile section now uses relative positioning - all states visible
  // Video has been moved to box section
  return;
}

function initTrimmerSection() {
  const section = document.querySelector(
    '.trimmer-desktop[id^="trimmer-section-"]'
  );
  if (!section) return;

  const isMobile = window.innerWidth <= 999;

  // On mobile, don't initialize scroll trigger (we use mobile observer instead)
  if (isMobile) {
    return;
  }

  // Get all elements by data-state
  const title = section.querySelector(".trimmer-title");
  const textWrapper = section.querySelector(
    ".trimmer-text[data-state='2-3-4']"
  );
  const textTrack = section.querySelector(".trimmer-text-track");
  const textState1 = section.querySelector(
    ".trimmer-text-state[data-state-index='1']"
  );
  const textState2 = section.querySelector(
    ".trimmer-text-state[data-state-index='2']"
  );
  const textState3 = section.querySelector(
    ".trimmer-text-state[data-state-index='3']"
  );
  const middleImages = section.querySelectorAll(".trimmer-image-wrapper img");

  // Wait for GSAP to be available
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    setTimeout(initTrimmerSection, 100);
    return;
  }

  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Set initial state (State 1)
  function setInitialState() {
    // Title: visible for all states
    if (title) {
      gsap.set(title, {
        opacity: 1,
        visibility: "visible",
      });
    }

    // Text wrapper: visible for all states
    if (textWrapper) {
      gsap.set(textWrapper, { opacity: 1, visibility: "visible" });
    }

    // Text track: start at translateY(50%) with opacity 0
    if (textTrack) {
      gsap.set(textTrack, { transform: "translateY(50%)", opacity: 0 });
    }

    // Text states: all at 0.3 opacity initially (no active text in state 1)
    if (textState1) {
      gsap.set(textState1, { opacity: 0.3 });
    }
    if (textState2) {
      gsap.set(textState2, { opacity: 0.3 });
    }
    if (textState3) {
      gsap.set(textState3, { opacity: 0.3 });
    }

    // Middle images: show state 1, hide others
    middleImages.forEach((img) => {
      const state = img.getAttribute("data-state");
      if (state === "1") {
        gsap.set(img, { opacity: 1, visibility: "visible", zIndex: 1 });
      } else {
        gsap.set(img, { opacity: 0, visibility: "hidden", zIndex: 0 });
      }
    });
  }

  // Initialize state 1
  setInitialState();

  // Progress bar removed
  let progressBar = null;

  // Create ScrollTrigger for state transitions
  ScrollTrigger.create({
    trigger: section,
    start: "top top",
    end: "+=400%", // 4x viewport height for 4 states
    pin: true,
    anticipatePin: 1,
    onUpdate: (self) => {
      const progress = self.progress;

      // Define transition points for 4 states with hold periods
      const state1End = 0.25; // State 1 → State 2 transition ends at 25%
      const state2HoldStart = 0.25; // State 2 hold period starts at 25%
      const state2HoldEnd = 0.4; // State 2 hold period ends at 40%
      const state3TransitionStart = 0.4; // State 2 → State 3 transition starts at 40%
      const state3End = 0.65; // State 2 → State 3 transition ends at 65%
      const state3HoldStart = 0.65; // State 3 hold period starts at 65%
      const state3HoldEnd = 0.8; // State 3 hold period ends at 80%
      const state4TransitionStart = 0.8; // State 3 → State 4 transition starts at 80%

      // Calculate values for each state
      let textTrackY; // Text track translateY value
      let textTrackOpacity; // Text track opacity value
      let textState1Opacity, textState2Opacity, textState3Opacity;
      let image1Opacity, image2Opacity, image34Opacity;
      let image1ZIndex, image2ZIndex, image34ZIndex;

      if (progress <= state1End) {
        // State 1 → State 2: Text track moves from 50% to 0% and fades in from opacity 0 to 1, text-1 becomes active
        const state1Progress = progress / state1End;

        // Text track: move from 50% to 0% and fade in from 0 to 1
        textTrackY = 50 - state1Progress * 50; // 50% to 0%
        textTrackOpacity = state1Progress; // 0 to 1

        // Text states: text-1 becomes active (opacity 1), others stay 0.3
        textState1Opacity = 0.3 + state1Progress * 0.7; // 0.3 to 1
        textState2Opacity = 0.3;
        textState3Opacity = 0.3;

        // Images: state 1 fades out, state 2 fades in
        image1Opacity = 1 - state1Progress;
        image2Opacity = state1Progress;
        image34Opacity = 0;
        image1ZIndex = image1Opacity > 0 ? 1 : 0;
        image2ZIndex = image2Opacity > 0 ? 2 : 0;
        image34ZIndex = 0;
      } else if (progress <= state2HoldEnd) {
        // State 2: Hold period - text track at 0% with opacity 1, text-1 active
        textTrackY = 0;
        textTrackOpacity = 1;
        textState1Opacity = 1;
        textState2Opacity = 0.3;
        textState3Opacity = 0.3;
        image1Opacity = 0;
        image2Opacity = 1;
        image34Opacity = 0;
        image1ZIndex = 0;
        image2ZIndex = 2;
        image34ZIndex = 0;
      } else if (progress <= state3End) {
        // State 2 → State 3: Text track moves from 0% to -100%, text-2 becomes active
        const state2Progress =
          (progress - state2HoldEnd) / (state3End - state2HoldEnd);

        textTrackY = 0 - state2Progress * 100; // 0% to -100%
        textTrackOpacity = 1; // Opacity stays at 1

        // Text states: text-1 fades to 0.3, text-2 fades to 1
        textState1Opacity = 1 - state2Progress * 0.7; // 1 to 0.3
        textState2Opacity = 0.3 + state2Progress * 0.7; // 0.3 to 1
        textState3Opacity = 0.3;

        // Images: state 2 fades out, state 3-4 fades in
        image1Opacity = 0;
        image2Opacity = 1 - state2Progress;
        image34Opacity = state2Progress;
        image1ZIndex = 0;
        image2ZIndex = image2Opacity > 0 ? 2 : 0;
        image34ZIndex = image34Opacity > 0 ? 3 : 0;
      } else if (progress <= state3HoldEnd) {
        // State 3: Hold period - text track at -100% with opacity 1, text-2 active
        textTrackY = -100;
        textTrackOpacity = 1;
        textState1Opacity = 0.3;
        textState2Opacity = 1;
        textState3Opacity = 0.3;
        image1Opacity = 0;
        image2Opacity = 0;
        image34Opacity = 1;
        image1ZIndex = 0;
        image2ZIndex = 0;
        image34ZIndex = 3;
      } else {
        // State 3 → State 4: Text track moves from -100% to -200%, text-3 becomes active
        const state3Progress = (progress - state3HoldEnd) / (1 - state3HoldEnd);

        textTrackY = -100 - state3Progress * 100; // -100% to -200%
        textTrackOpacity = 1; // Opacity stays at 1

        // Text states: text-2 fades to 0.3, text-3 fades to 1
        textState1Opacity = 0.3;
        textState2Opacity = 1 - state3Progress * 0.7; // 1 to 0.3
        textState3Opacity = 0.3 + state3Progress * 0.7; // 0.3 to 1

        // Images: state 3-4 stays visible (same image for both states)
        image1Opacity = 0;
        image2Opacity = 0;
        image34Opacity = 1;
        image1ZIndex = 0;
        image2ZIndex = 0;
        image34ZIndex = 3;
      }

      // Update text track
      if (textTrack) {
        gsap.to(textTrack, {
          transform: `translateY(${textTrackY}%)`,
          opacity: textTrackOpacity,
          duration: 0.1,
          ease: "none",
        });
      }

      // Update text state opacities
      if (textState1) {
        gsap.to(textState1, {
          opacity: textState1Opacity,
          duration: 0.1,
          ease: "none",
        });
      }
      if (textState2) {
        gsap.to(textState2, {
          opacity: textState2Opacity,
          duration: 0.1,
          ease: "none",
        });
      }
      if (textState3) {
        gsap.to(textState3, {
          opacity: textState3Opacity,
          duration: 0.1,
          ease: "none",
        });
      }

      // Update middle images
      middleImages.forEach((img) => {
        const state = img.getAttribute("data-state");
        let targetOpacity = 0;
        let targetVisibility = "hidden";
        let targetZIndex = 0;

        if (state === "1") {
          targetOpacity = image1Opacity;
          targetVisibility = image1Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image1ZIndex;
        } else if (state === "2") {
          targetOpacity = image2Opacity;
          targetVisibility = image2Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image2ZIndex;
        } else if (state === "3-4") {
          targetOpacity = image34Opacity;
          targetVisibility = image34Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image34ZIndex;
        }

        gsap.to(img, {
          opacity: targetOpacity,
          visibility: targetVisibility,
          zIndex: targetZIndex,
          duration: 0.1,
          ease: "none",
        });
      });

      // Progress bar removed
    },
  });

  // Handle window resize
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
      if (window.innerWidth <= 999) {
        // Mobile: no initialization needed
      } else {
        // Desktop: reinitialize
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
        initTrimmerSection();
      }
    }, 250);
  });
}

// Initialize when DOM is ready
function initialize() {
  // Use global scroll-to-top function if available, otherwise execute immediately
  if (typeof window.ensureScrollToTop === "function") {
    window.ensureScrollToTop(() => {
      initTrimmerSection();
      initTrimmerMobileSection();
    });
  } else {
    // Fallback: wait a bit for global.js to load
    setTimeout(() => {
      if (typeof window.ensureScrollToTop === "function") {
        window.ensureScrollToTop(() => {
          initTrimmerSection();
          initTrimmerMobileSection();
        });
      } else {
        initTrimmerSection();
        initTrimmerMobileSection();
      }
    }, 100);
  }
}

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initialize);
} else {
  initialize();
}

// Handle browser back/forward navigation
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    if (typeof window.ensureScrollToTop === "function") {
      window.ensureScrollToTop(() => {
        const section = document.querySelector(
          '.trimmer-desktop[id^="trimmer-section-"]'
        );
        if (section && typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.getAll().forEach((trigger) => {
            if (trigger.trigger === section) {
              trigger.kill();
            }
          });
        }
        initTrimmerSection();
        initTrimmerMobileSection();
      });
    } else {
      const section = document.querySelector(
        '.trimmer-desktop[id^="trimmer-section-"]'
      );
      if (section && typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
      }
      initTrimmerSection();
      initTrimmerMobileSection();
    }
  }
});
